import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class EventService {

  private selectedEventSource = new BehaviorSubject<Event>(null);
  selectedEvent = this.selectedEventSource.asObservable();

  private activeEventSource = new BehaviorSubject<Event>(null);
  activeEvent = this.activeEventSource.asObservable();

  private closedEventsSource = new BehaviorSubject<Event[]>(null);
  closedEvents = this.closedEventsSource.asObservable();

  private closedEventsGroupSource = new BehaviorSubject<EventByYear[]>(null);
  closedEventsGroups = this.closedEventsGroupSource.asObservable();

  private eventPackageSource = new BehaviorSubject<EventPackage>(null);
  eventPackage = this.eventPackageSource.asObservable();

  private eventPackagesSource = new BehaviorSubject<EventPackage[]>(null);
  eventPackages = this.eventPackagesSource.asObservable();

  private eventRequestAllocationsSource = new BehaviorSubject<EventRequestAllocation[]>(null);
  eventRequestAllocations = this.eventRequestAllocationsSource.asObservable();

  private eventRequestFacilitiesSource = new BehaviorSubject<EventRequestAllocationFacility[]>(null);
  eventRequestFacilities = this.eventRequestFacilitiesSource.asObservable();

  constructor(
    @Inject('BASE_URL') private baseUrl: string,
    private http: HttpClient
  ) { }

  getActiveEvent() {
    this.http.get<Event>(`${this.baseUrl}api/event/active`).subscribe(
      response => {
        this.activeEventSource.next(response);
      },
      error => console.error(error)
    );
  }

  getClosedEvents() {
    this.http.get<Event[]>(`${this.baseUrl}api/event/closed`)
      .subscribe(
        response => {
          this.closedEventsSource.next(response);
        },
        error => console.error(error)
      );
  }

  getClosedEventsGrouped() {
    this.http.get<EventByYear[]>(`${this.baseUrl}api/event/closedByYear`).subscribe(
      response => {
        this.closedEventsGroupSource.next(response);
      },
      error => console.error(error)
    );
  }

  getEvent(eventId: number) {
    this.http.get<Event>(`${this.baseUrl}api/event/${eventId}`).subscribe(
      response => {
        this.selectedEventSource.next(response);
      },
      error => console.error(error)
    );
  }

  getEventPackage(packageId: number) {
    this.http.get<EventPackage>(`${this.baseUrl}api/event/package/${packageId}`).subscribe(
      response => {
        if (response) {
          this.eventPackageSource.next(response);
        }
      },
      error => console.error(error)
    );
  }

  getEventPackages(eventId: number) {
    this.http.get<EventPackage[]>(`${this.baseUrl}api/event/${eventId}/getPackages`).subscribe(
      response => {
        if (response) {
          this.eventPackagesSource.next(response);
        }
      },
      error => console.error(error)
    );
  }

  getEventPackageDetails(eventId: number, packageId: number) {
    this.http.get<EventRequestAllocation[]>(`${this.baseUrl}api/event/${eventId}/${packageId}/details`).subscribe(
      response => {
        if (response) {
          this.eventRequestAllocationsSource.next(response);
        }
      },
      error => console.error(error)
    );
  }

  getEventRequestAllocationFacilities(requestId: number, allocationId?: number) {
    let url = `${this.baseUrl}api/eventRequest/${requestId}`;
    if (!!allocationId) {
      url = url + `/${allocationId}`;
    }
    url = url + `/facilities`;
    this.http.get<EventRequestAllocationFacility[]>(url).subscribe(
      response => {
        if (response) {
          this.eventRequestFacilitiesSource.next(response);
        }
      },
      error => console.error(error)
    );
  }

  getAvailableRequestAllocationFacilities(permitAnnualOperatingPlanId: number) {
    this.http.get<EventRequestAllocationFacility[]>(`${this.baseUrl}api/eventRequest/facilities?permitAnnualOperatingPlanId=${permitAnnualOperatingPlanId}`).subscribe(
      response => {
        if (response) {
          this.eventRequestFacilitiesSource.next(response);
        }
      },
      error => console.error(error)
    );
  }

  createEventObj(data: Event) {
    this.http.post<Event>(`${this.baseUrl}api/event`, data).subscribe(
      response => {
        if (response) {
          this.activeEventSource.next(response);
        }
      },
      error => {
        if (error) {
          console.error(error)
        } else {
          console.error('Unknown Error')
        }
      }
    );
  }

  createEventPackage(data: EventPackage) {
    var eventId = data.eventId;
    this.http.post<EventPackage>(`${this.baseUrl}api/event/${eventId}/createPackage`, data).subscribe(
      response => {
        if (response) {
          this.getEventPackages(eventId);
        }
      },
      error => {
        if (error) {
          console.error(error)
        } else {
          console.error('Unknown Error')
        }
      }
    );
  }

  createEventRequest(request: EventRequestAllocation, facilities: EventRequestAllocationFacility[], eventPackage: EventPackage) {
    var eventId = eventPackage.eventId;
    let eventRequestPost: EventRequestPost = {
      eventId: eventPackage.eventId,
      eventPackageId: eventPackage.eventRequestPackageId,
      permitAnnualOperatingPlanId: eventPackage.permitAnnualOperatingPlanId,
      requestAmount: request.requestAmount,
      requestDate: request.requestDate,
      requestComment: request.requestComment,
      facilities: facilities
    }
    this.http.post<EventRequestAllocation>(`${this.baseUrl}api/eventRequest`, eventRequestPost).subscribe(
      response => {
        if (response) {
          this.getEventPackages(eventId);
        }
      },
      error => {
        if (error) {
          console.error(error)
        } else {
          console.error('Unknown Error');
        }
      }
    )
  }

  allocateEventRequest(allocation: EventRequestAllocation, facilities: EventRequestAllocationFacility[], eventPackage: EventPackage) {
    var eventId = eventPackage.eventId;
    let eventAllocationPost: EventAllocationPost = {
      allocationAmount: allocation.allocationAmount,
      allocationDate: allocation.allocationDate,
      allocationComment: allocation.allocationComment,
      externalNoticeId: allocation.externalNoticeId,
      facilities: facilities
    }
    this.http.post<EventRequestAllocation>(`${this.baseUrl}api/eventRequest/${allocation.eventRequestId}/allocation`, eventAllocationPost).subscribe(
      response => {
        if (response) {
          this.getEventPackages(eventId);
        }
      },
      error => {
        if (error) {
          console.error(error)
        } else {
          console.error('Unknown Error');
        }
      }
    )
  }

  endEvent(data: Event) {
    var today = new Date();
    data.eventEndDate = today;
    this.http.put<Event>(`${this.baseUrl}api/event/${data.eventId}`, data).subscribe(
      response => {
        if (response) {
          this.getActiveEvent();
          this.getClosedEvents();
        }
      },
      error => {
        if (error) {
          console.error(error);
        } else {
          console.error('Unknown Error');
        }
      }
    )
  }

}

export interface Event {
  eventId: number,
  eventName: string,
  eventDescription: string,
  eventJustification: string,
  eventStartDate: Date,
  eventEndDate: Date,
  eventRequestPackageIds: number[]
}

export interface EventByYear {
  year: number,
  events: Event[]
}

export interface EventPackage {
  eventRequestPackageId: number,
  status: string,
  eventId: number,
  permitAnnualOperatingPlanId: number,
  applicationPackageId: number,
  appropriation: string,
  appropriator: string,
  diversion: string,
  grantAmount?: number,
  canRequest: boolean,
  requestAmount?: number,
  requestStartDate?: Date,
  requestEndDate?: Date,
  canAllocate: boolean,
  allocationAmount?: number,
  allocationDate?: Date,
  requestAllocations: EventRequestAllocation[],
  showDetail: boolean
}

export interface EventRequestAllocation {
  eventPackageId: number,
  eventRequestId: number,
  eventAllocationId?: number,
  requestAmount?: number,
  requestDate?: Date,
  requestComment: string,
  allocationAmount?: number,
  allocationDate?: Date,
  allocationComment: string,
  externalNoticeId: string
}

export interface EventRequestAllocationFacility {
  eventRequestAllocationProjectFacilityId: number,
  eventRequestId: number,
  eventAllocationId: number,
  projectFacilityAnnualOperatingPlanId: number,
  facilityName: string,
  facilityDiversionRate: number,
  appropriatorRequested: boolean,
  dnrApproved: boolean
}

export interface EventRequestPost {
  eventId: number,
  eventPackageId: number,
  permitAnnualOperatingPlanId: number,
  requestAmount: number,
  requestDate: Date,
  requestComment: string,
  facilities: EventRequestAllocationFacility[]
}

export interface EventAllocationPost {
  allocationAmount: number,
  allocationDate: Date,
  allocationComment: string,
  externalNoticeId: string,
  facilities: EventRequestAllocationFacility[]
}
